import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Upload,
  InputNumber,
  message,
  Row,
  Col,
  Divider,
  Typography,
  Spin,
  Space,
} from 'antd';
import { UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../services/categoryService';
import { storeAPI } from '../services/api';
import { uploadService } from '../services/uploadService';
import type { Category } from '../types';
import PageShell from '../components/Layout/PageShell';

const { Text } = Typography;

const { Option } = Select;
const { TextArea } = Input;

const CreateStore: React.FC = () => {
  const navigate = useNavigate();
  // const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await categoryService.getAllActiveCategories();
      setCategories(response);
    } catch (error) {
      console.error('Error loading categories:', error);
      message.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleLogoUpload = async (file: File) => {
    try {
      // Validate file
      if (!uploadService.validateImageFile(file)) {
        message.error('Please upload a valid image file (JPEG, PNG, WebP)');
        return false;
      }

      if (!uploadService.validateFileSize(file)) {
        message.error('File size must be less than 10MB');
        return false;
      }

      setLogoFile(file);
      const result = await uploadService.uploadImage(file);
      setLogoUrl(result.url);
      message.success('Logo uploaded successfully');
      return false; // Prevent default upload behavior
    } catch (error: any) {
      message.error(error.message || 'Failed to upload logo');
      return false;
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      console.log('Creating store with data:', values);
      
      // Prepare store data for API
      const storeData = {
        storeName: values.storeName,
        description: values.description,
        tagline: values.tagline,
        categories: values.categories,
        logo: logoUrl, // Include uploaded logo URL
        businessInfo: {
          businessType: values.businessInfo?.businessType,
          phone: values.businessInfo?.phone,
          email: values.businessInfo?.email,
          alternatePhone: values.businessInfo?.alternatePhone,
          website: values.businessInfo?.website,
          yearEstablished: values.businessInfo?.yearEstablished ? parseInt(values.businessInfo.yearEstablished) : undefined,
          registrationNumber: values.businessInfo?.registrationNumber,
          gstNumber: values.businessInfo?.gstNumber,
          panNumber: values.businessInfo?.panNumber,
        },
        address: {
          street: values.address?.street,
          city: values.address?.city,
          state: values.address?.state,
          zipCode: values.address?.zipCode,
          landmark: values.address?.landmark,
          country: 'India'
        }
      };

      // Remove undefined values
      Object.keys(storeData.businessInfo).forEach(key => {
        if (storeData.businessInfo[key as keyof typeof storeData.businessInfo] === undefined) {
          delete storeData.businessInfo[key as keyof typeof storeData.businessInfo];
        }
      });

      // Call the store creation API
      const response = await storeAPI.create(storeData);
      console.log('Store created successfully:', response.data);
      
      message.success('Store created successfully! It will be reviewed by admin before going live.');
      navigate('/stores');
    } catch (error: any) {
      console.error('Store creation error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to create store. Please try again.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const businessTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'proprietorship', label: 'Proprietorship' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'pvt_ltd', label: 'Private Limited' },
    { value: 'llp', label: 'LLP' },
    { value: 'other', label: 'Other' },
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Chandigarh', 'Puducherry'
  ];

  const headerActions = (
    <Space wrap size={8} className="w-full min-[480px]:w-auto">
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
        onClick={() => navigate('/stores')}
      >
        My stores
      </Button>
    </Space>
  );

  return (
    <PageShell
      title="Create store"
      description="Add your storefront details below. After you submit, an admin reviews it before it can go live."
      titleEnd={headerActions}
    >
      <Card className="vendor-surface-card w-full border border-slate-200/80 shadow-sm rounded-xl">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="vendor-comfort-form w-full max-w-full"
          requiredMark="optional"
        >
          {/* Basic Store Information */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Basic store information
            </Text>
          </Divider>
          
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="storeName"
                label="Store Name"
                rules={[{ required: true, message: 'Please enter store name' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter store name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="tagline"
                label="Tagline"
                rules={[{ max: 150, message: 'Tagline cannot exceed 150 characters' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter store tagline" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Store Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea size="large" className="w-full" rows={4} placeholder="Describe your store" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="categories"
                label="Categories"
                rules={[{ required: true, message: 'Please select at least one category' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  mode="multiple"
                  placeholder={categoriesLoading ? 'Loading categories...' : 'Select categories'}
                  showSearch
                  loading={categoriesLoading}
                  filterOption={(input, option) =>
                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={categoriesLoading ? <Spin size="small" /> : 'No categories found'}
                >
                  {categories.map((category) => (
                    <Option key={category._id} value={category._id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="logo"
                label="Store Logo"
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={handleLogoUpload}
                  onRemove={() => {
                    setLogoFile(null);
                    setLogoUrl('');
                    return true;
                  }}
                  fileList={logoFile ? [{
                    uid: '1',
                    name: logoFile.name,
                    status: 'done',
                    url: logoUrl
                  }] : []}
                >
                  {!logoFile && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload Logo</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          {/* Business Information */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Business information
            </Text>
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'businessType']}
                label="Business Type"
                rules={[{ required: true, message: 'Please select business type' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  placeholder="Select business type"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {businessTypes.map((type) => (
                    <Option key={type.value} value={type.value}>
                      {type.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'phone']}
                label="Business Phone"
                rules={[
                  { required: true, message: 'Please enter business phone' },
                  { pattern: /^[6-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
                ]}
              >
                <Input
                  size="large"
                  className="w-full"
                  placeholder="Enter business phone number"
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'email']}
                label="Business Email"
                rules={[
                  { required: true, message: 'Please enter business email' },
                  { type: 'email', message: 'Please enter valid email' },
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter business email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'alternatePhone']}
                label="Alternate Phone"
                rules={[
                  { pattern: /^[6-9]\d{9}$/, message: 'Please enter a valid 10-digit phone number' }
                ]}
              >
                <Input
                  size="large"
                  className="w-full"
                  placeholder="Enter alternate phone number"
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'website']}
                label="Website"
                rules={[
                  { type: 'url', message: 'Please enter a valid website URL' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter website URL" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'yearEstablished']}
                label="Year Established"
                rules={[
                  { type: 'number', min: 1900, max: new Date().getFullYear(), message: 'Please enter a valid year' }
                ]}
              >
                <InputNumber
                  size="large"
                  className="w-full !max-w-full"
                  placeholder="Enter year established"
                  min={1900}
                  max={new Date().getFullYear()}
                  precision={0}
                  controls={false}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'registrationNumber']}
                label="Registration Number"
                rules={[
                  { min: 5, max: 50, message: 'Registration number must be between 5 and 50 characters' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter registration number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['businessInfo', 'gstNumber']}
                label="GST Number"
                rules={[
                  { pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, message: 'Please enter a valid GST number' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter GST number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name={['businessInfo', 'panNumber']}
            label="PAN Number"
            rules={[
              { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Please enter a valid PAN number' }
            ]}
          >
            <Input size="large" className="w-full" placeholder="Enter PAN number" />
          </Form.Item>

          {/* Store Address */}
          <Divider orientation="left" plain className="border-slate-200">
            <Text strong className="text-slate-800">
              Store address
            </Text>
          </Divider>

          <Form.Item
            name={['address', 'street']}
            label="Street Address"
            rules={[{ required: true, message: 'Please enter street address' }]}
          >
            <Input size="large" className="w-full" placeholder="Enter street address" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'city']}
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input size="large" className="w-full" placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'state']}
                label="State"
                rules={[{ required: true, message: 'Please select state' }]}
              >
                <Select
                  size="large"
                  className="w-full"
                  placeholder="Select state"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children).toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {indianStates.map((state) => (
                    <Option key={state} value={state}>
                      {state}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name={['address', 'zipCode']}
                label="ZIP Code"
                rules={[
                  { required: true, message: 'Please enter ZIP code' },
                  { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit ZIP code' }
                ]}
              >
                <Input size="large" className="w-full" placeholder="Enter ZIP code" maxLength={6} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name={['address', 'landmark']}
            label="Landmark"
            rules={[
              { max: 100, message: 'Landmark cannot exceed 100 characters' }
            ]}
          >
            <Input size="large" className="w-full" placeholder="Enter landmark (optional)" />
          </Form.Item>

          <Form.Item className="mb-0 mt-2">
            <div className="flex w-full flex-col-reverse gap-3 pt-4 sm:flex-row sm:flex-wrap sm:justify-end sm:pt-6">
              <Button
                onClick={() => navigate('/stores')}
                size="large"
                className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="w-full min-[480px]:w-auto border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md transition-shadow hover:shadow-lg"
              >
                Create store
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Card>
    </PageShell>
  );
};

export default CreateStore;
